import { useLayoutEffect, useRef } from "react";
import { gsap, ScrollTrigger } from "./gsap";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : () => {};

/**
 * Run a GSAP animation scoped to a container element.
 *
 * The callback receives (gsap, containerElement) and can return nothing;
 * every tween/ScrollTrigger created inside is captured by a gsap.context()
 * that is reverted on unmount or when `deps` change, so React re-renders
 * and route changes never leave stale animations behind.
 */
export function useGsap(callback, deps = []) {
    const scope = useRef(null);

    useIsomorphicLayoutEffect(() => {
        if (!scope.current) return undefined;
        const ctx = gsap.context(() => callback(gsap, scope.current), scope);
        return () => ctx.revert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return scope;
}

/**
 * Fade-and-rise the container (or its `[data-reveal]` children, staggered)
 * the first time it scrolls into view.
 */
export function useReveal({ y = 36, stagger = 0.12, duration = 0.9, delay = 0, once = true, start } = {}, deps = []) {
    return useGsap((g, el) => {
        const items = el.querySelectorAll("[data-reveal]");
        const targets = items.length ? Array.from(items) : el;
        g.from(targets, {
            y,
            autoAlpha: 0,
            duration,
            delay,
            stagger,
            clearProps: "transform",
            scrollTrigger: {
                trigger: el,
                once,
                ...(start ? { start } : {}),
            },
        });
    }, deps);
}

/**
 * Count a number element from 0 up to `value` when it scrolls into view.
 */
export function useCountUp(value, { duration = 2, suffix = "", prefix = "", decimals = 0, locale = true } = {}) {
    const ref = useRef(null);

    useIsomorphicLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const target = Number(value) || 0;
        const state = { value: 0 };
        const format = (n) => {
            const fixed = n.toFixed(decimals);
            const text = locale ? Number(fixed).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : fixed;
            return `${prefix}${text}${suffix}`;
        };
        el.textContent = format(0);
        const tween = gsap.to(state, {
            value: target,
            duration,
            ease: "power2.out",
            onUpdate: () => { el.textContent = format(state.value); },
            scrollTrigger: { trigger: el, once: true },
        });
        return () => {
            if (tween.scrollTrigger) tween.scrollTrigger.kill();
            tween.kill();
        };
    }, [value, duration, suffix, prefix, decimals, locale]);

    return ref;
}


/**
 * Reveal grid items individually as each scrolls into view, staggering the
 * ones that arrive together. Safe to re-run when `deps` change (for example
 * after "load more" appends cards): items already revealed are skipped.
 */
export function useBatchReveal({ y = 36, stagger = 0.1, duration = 0.8, selector = "[data-reveal]" } = {}, deps = []) {
    return useGsap((g, el) => {
        const items = Array.from(el.querySelectorAll(`${selector}:not([data-revealed])`));
        if (!items.length) return;
        items.forEach((item) => item.setAttribute("data-revealed", ""));
        g.set(items, { y, autoAlpha: 0 });
        ScrollTrigger.batch(items, {
            once: true,
            onEnter: (batch) => g.to(batch, { y: 0, autoAlpha: 1, duration, stagger, clearProps: "transform", overwrite: true }),
        });
    }, deps);
}

export { gsap, ScrollTrigger };
